import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import ImageUploadField from '../../components/common/ImageUploadField';
import '../../styles/admin-pages.css';
import './CreatorSection.css';

type Disclosure = 'private' | 'teaser' | 'full';

interface MyTemplate {
  node_id: string;
  label: string;
  disclosure: Disclosure;
  teaser: string | null;
  status: string;
}

interface MyCreator {
  id: string;
  slug: string;
  status: string;
  rank_code: string;
  rank_label: string | null;
  effective_share_rate: number | null;
  public_display_name: string;
  avatar_url: string | null;
  bio: string | null;
  website_url: string | null;
  x_url: string | null;
  note_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  line_url: string | null;
  tiktok_url: string | null;
  facebook_url: string | null;
  templates: MyTemplate[];
}

interface MeResponse { is_creator: boolean; creator?: MyCreator; }
interface Summary { year_month: string; usage_count: number; paid_credits_consumed: number; estimated_share_jpy: number; }

const URL_FIELDS: { key: keyof MyCreator; label: string }[] = [
  { key: 'website_url', label: 'Webサイト' }, { key: 'x_url', label: 'X' },
  { key: 'note_url', label: 'note' }, { key: 'instagram_url', label: 'Instagram' },
  { key: 'youtube_url', label: 'YouTube' }, { key: 'line_url', label: 'LINE' },
  { key: 'tiktok_url', label: 'TikTok' }, { key: 'facebook_url', label: 'Facebook' },
];

const DISCLOSURE_LABELS: Record<Disclosure, string> = {
  private: '非公開', teaser: 'リード文のみ公開', full: '全文公開',
};

export default function CreatorSection() {
  const queryClient = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ['acp-me'],
    queryFn: async () => (await apiClient.get<MeResponse>('/acp/me')).data,
  });
  const { data: summary } = useQuery({
    queryKey: ['acp-me-summary'],
    enabled: !!me?.is_creator,
    queryFn: async () => (await apiClient.get<Summary>('/acp/me/summary')).data,
  });

  if (!me?.is_creator || !me.creator) return null;
  return <CreatorSectionInner creator={me.creator} summary={summary} onSaved={() => queryClient.invalidateQueries({ queryKey: ['acp-me'] })} />;
}

function CreatorSectionInner({ creator, summary, onSaved }: { creator: MyCreator; summary?: Summary; onSaved: () => void }) {
  const [form, setForm] = useState(() => pickProfile(creator));
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  useEffect(() => setForm(pickProfile(creator)), [creator]);

  const saveProfile = useMutation({
    mutationFn: async () => apiClient.patch('/acp/me', form),
    onSuccess: () => { setProfileMsg('保存しました'); onSaved(); },
    onError: () => setProfileMsg('保存に失敗しました'),
  });

  const setField = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const ratePct = creator.effective_share_rate == null ? '—' : `${(creator.effective_share_rate * 100).toFixed(0)}%`;

  return (
    <div className="acp-creator-section">
      <h2 className="profile-section-title">クリエイター</h2>

      <div className="acp-creator-meta">
        <span className={`acp-rank acp-rank--${creator.rank_code}`}>{creator.rank_label ?? creator.rank_code}</span>
        <span className="acp-meta-item">レベニューシェア率 {ratePct}</span>
        <a className="acp-meta-link" href={`/creators/${creator.slug}`} target="_blank" rel="noopener noreferrer">
          公開プロフィール /creators/{creator.slug}
        </a>
      </div>

      {summary && (
        <div className="acp-summary">
          <div className="acp-summary-tile"><span className="acp-summary-num">{summary.usage_count}</span><span className="acp-summary-cap">今月の使用回数</span></div>
          <div className="acp-summary-tile"><span className="acp-summary-num">{summary.paid_credits_consumed.toLocaleString()}</span><span className="acp-summary-cap">対象ポイント</span></div>
          <div className="acp-summary-tile"><span className="acp-summary-num">¥{summary.estimated_share_jpy.toLocaleString()}</span><span className="acp-summary-cap">RS 見込 (概算)</span></div>
        </div>
      )}
      <p className="acp-note">実績は当月の暫定値です。確定額は月次精算で決まります。</p>

      {/* プロフィール編集 */}
      <div className="admin-form-section">
        <h3 className="acp-subtitle">プロフィール</h3>
        <div className="admin-form-group">
          <label className="admin-form-label">公開名義</label>
          <input className="admin-form-input" value={form.public_display_name} onChange={(e) => setField('public_display_name', e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label className="admin-form-label">プロフィール文</label>
          <textarea className="admin-form-textarea" rows={3} value={form.bio} onChange={(e) => setField('bio', e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label className="admin-form-label">アイコン</label>
          <ImageUploadField value={form.avatar_url} onChange={(url) => setField('avatar_url', url)} />
        </div>
        <div className="acp-url-grid">
          {URL_FIELDS.map(({ key, label }) => (
            <div className="admin-form-group" key={key}>
              <label className="admin-form-label">{label}</label>
              <input className="admin-form-input" value={form[key as keyof typeof form]} onChange={(e) => setField(key as keyof typeof form, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="acp-actions">
          <button className="admin-btn admin-btn-primary" onClick={() => { setProfileMsg(null); saveProfile.mutate(); }} disabled={saveProfile.isPending}>
            プロフィールを保存
          </button>
          {profileMsg && <span className="acp-msg">{profileMsg}</span>}
        </div>
      </div>

      {/* テンプレごとの開示設定 */}
      <div className="admin-form-section">
        <h3 className="acp-subtitle">提供テンプレートの公開設定</h3>
        {creator.templates.length === 0 ? (
          <p className="acp-note">紐付けられたテンプレートはありません。</p>
        ) : (
          creator.templates.map((t) => <TemplateDisclosureRow key={t.node_id} template={t} onSaved={onSaved} />)
        )}
      </div>
    </div>
  );
}

function TemplateDisclosureRow({ template, onSaved }: { template: MyTemplate; onSaved: () => void }) {
  const [disclosure, setDisclosure] = useState<Disclosure>(template.disclosure);
  const [teaser, setTeaser] = useState(template.teaser ?? '');
  const [msg, setMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      apiClient.patch(`/acp/me/templates/${template.node_id}/disclosure`, { disclosure, teaser }),
    onSuccess: () => { setMsg('保存しました'); onSaved(); },
    onError: () => setMsg('保存に失敗しました'),
  });

  return (
    <div className="acp-template-row">
      <div className="acp-template-head">
        <span className="acp-template-label">{template.label}</span>
        {template.status !== 'active' && <span className="acp-template-inactive">非公開中</span>}
      </div>
      <div className="acp-template-controls">
        <select className="admin-form-select" value={disclosure} onChange={(e) => setDisclosure(e.target.value as Disclosure)}>
          {(['private', 'teaser', 'full'] as Disclosure[]).map((d) => (
            <option key={d} value={d}>{DISCLOSURE_LABELS[d]}</option>
          ))}
        </select>
        {disclosure === 'teaser' && (
          <textarea className="admin-form-textarea" rows={2} placeholder="公開するリード文（プロンプト冒頭の紹介など）" value={teaser} onChange={(e) => setTeaser(e.target.value)} />
        )}
        <div className="acp-actions">
          <button className="admin-btn admin-btn-sm admin-btn-primary" onClick={() => { setMsg(null); save.mutate(); }} disabled={save.isPending}>保存</button>
          {msg && <span className="acp-msg">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function pickProfile(c: MyCreator) {
  return {
    public_display_name: c.public_display_name ?? '',
    bio: c.bio ?? '',
    avatar_url: c.avatar_url ?? '',
    website_url: c.website_url ?? '', x_url: c.x_url ?? '', note_url: c.note_url ?? '',
    instagram_url: c.instagram_url ?? '', youtube_url: c.youtube_url ?? '',
    line_url: c.line_url ?? '', tiktok_url: c.tiktok_url ?? '', facebook_url: c.facebook_url ?? '',
  };
}
